import React, { useState, useEffect } from 'react';
import { useAlert } from '../components/ConfirmModal';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import usePricing from '../utils/usePricing';
import SEO from '../components/SEO';
import { trackBeginCheckout, trackSubscriptionChanged, trackPricingBillingToggled } from '../utils/analytics';
import { getAffiliateCookieCode } from '../utils/affiliate';
import SchemaMarkup, { buildFaqSchema, buildProductSchema } from '../components/SchemaMarkup';
import { openCheckoutUrl } from '../utils/twa';
import { shouldUseIap, purchaseIap, getIapProductInfo, getIntroEligibility, paymentStoreName } from '../utils/iap';
import { isIOS } from '../utils/capacitor';
import ExitIntentPopup from '../components/ExitIntentPopup';
import LeadEmailForm from '../components/LeadEmailForm';
import { entitledPlan, isViaCoParent } from '../utils/entitlements';

const PRICING_FAQS = [
  { q: 'Can I cancel my subscription at any time?', a: 'Yes. Cancel any time from your account settings — no questions asked, no cancellation fees.' },
  { q: 'What happens when I reach my free story limit?', a: 'Free plan users get 5 stories per month. Once you reach the limit you can upgrade to a paid plan or wait until next month.' },
  { q: 'Is there a family plan?', a: 'Yes. The Family plan supports unlimited children profiles and unlimited stories, perfect for families with multiple children.' },
  { q: 'Is there a free plan?', a: 'Yes — the Free plan gives you 5 stories every month forever with no credit card required. No time limit, no commitment.' },
  { q: 'What payment methods do you accept?', a: 'We accept all major credit and debit cards, as well as Apple Pay and Google Pay, processed securely via Stripe.' },
  { q: 'Will I be charged immediately when I upgrade?', a: 'Yes, the first payment is taken immediately when you subscribe. Subsequent payments renew monthly or annually depending on your billing cycle.' },
  { q: 'Can I switch between monthly and annual billing?', a: 'Yes. You can switch billing cycles from your account settings at any time.' },
  { q: 'Are my stories saved if I downgrade?', a: 'Yes. Your existing story library is always preserved regardless of your plan.' },
];

const PricingPage = () => {
  const alert = useAlert();
  const { user, refreshUser } = useAuth();
  const { pricing } = usePricing();
  const [loading,          setLoading]          = useState(false);
  const [billing,          setBilling]          = useState('annual'); // default to annual to show savings
  const navigate = useNavigate();

  const isAnnual = billing === 'annual';

  const plans = [
    {
      id: 'free', name: 'Free', price: 0, period: 'forever',
      color: '#6c757d', bg: '#f8f9fa', btnClass: 'btn-outline-secondary',
      tag: null,
      features: [
        { text: '5 stories per month', ok: true },
        { text: '1 child profile', ok: true },
        { text: 'Story library (saved forever)', ok: true },
        { text: 'Magical illustrations', ok: true },
        { text: 'Browser read-aloud', ok: true },
        { text: 'All 11 languages', ok: true },
        { text: 'Bedtime reminders', ok: true },
        { text: 'Reading reports & monthly activity', ok: true },
        { text: 'Seasonal & holiday story themes', ok: true },
        { text: 'Smart narration', ok: false },
        { text: 'Story Quiz (comprehension questions)', ok: false },
        { text: 'Real-world learning topics in stories', ok: false },
        { text: 'Family Stories — all siblings as heroes', ok: false },
        { text: 'PDF export', ok: false },
        { text: 'Multiple child profiles', ok: false },
        { text: 'Grandparent sharing', ok: false },
        { text: 'Regenerate illustrations', ok: false },
        { text: '📸 Photo illustrations — your child\'s face in stories', ok: false },
      ],
    },
    {
      id: 'basic', name: 'Basic',
      color: '#6f42c1', bg: '#f8f5ff', btnClass: 'btn-dream',
      tag: null,
      features: [
        { text: '60 stories per month', ok: true },
        { text: 'Up to 3 child profiles', ok: true },
        { text: 'Story library (saved forever)', ok: true },
        { text: 'Magical illustrations + 20 regenerations/mo', ok: true },
        { text: 'High-quality narration (30/mo)', ok: true },
        { text: 'All 11 languages', ok: true },
        { text: 'Bedtime reminders + weekly digest', ok: true },
        { text: 'Reading reports & monthly activity', ok: true },
        { text: 'Story Series — multi-chapter adventures', ok: true },
        { text: 'Story Quiz — 3 comprehension questions per story', ok: true },
        { text: 'Real-world learning topics (planets, fractions…)', ok: true },
        { text: 'Family Stories — all siblings as heroes', ok: true },
        { text: 'Seasonal & holiday story themes', ok: true },
        { text: 'PDF export', ok: true },
        { text: 'Full theme scheduler', ok: true },
        { text: 'Grandparent sharing', ok: false },
        { text: 'Unlimited child profiles', ok: false },
        { text: 'Voice cloning narration', ok: false },
        { text: '📸 Photo illustrations — your child\'s face in stories (3/mo)', ok: true },
      ],
    },
    {
      id: 'family', name: 'Family',
      color: '#fd7e14', bg: '#fff8f0', btnClass: 'btn-warning',
      tag: '⭐ Most Popular',
      features: [
        { text: '120 stories per month', ok: true },
        { text: 'Unlimited child profiles', ok: true },
        { text: '🎙️ Voice cloning — narrate in your voice (5/mo)', ok: true },
        { text: 'Reading reports & monthly activity', ok: true },
        { text: 'Smart narration (40/mo)', ok: true },
        { text: 'Magical illustrations + 40 regenerations/mo', ok: true },
        { text: 'Story Series — multi-chapter adventures', ok: true },
        { text: 'Story Quiz — 3 comprehension questions per story', ok: true },
        { text: 'Real-world learning topics (planets, fractions…)', ok: true },
        { text: 'Family Stories — all siblings as heroes', ok: true },
        { text: 'Seasonal & holiday story themes', ok: true },
        { text: 'Grandparent sharing links', ok: true },
        { text: 'Reactions & view counts', ok: true },
        { text: 'PDF export', ok: true },
        { text: 'All 11 languages', ok: true },
        { text: 'Full theme scheduler', ok: true },
        { text: 'Early access to new features', ok: true },
        { text: 'Priority email support', ok: true },
        { text: '📸 Photo illustrations — your child\'s face in stories (6/mo)', ok: true },
      ],
    },
  ];

  // Why this device's store must not sell a plan to this account, or null.
  const appPurchaseBlock = (planId) => {
    const planName = plans.find(p => p.id === planId)?.name || planId;
    // A co-parent's access is the primary parent's plan; their own
    // subscription would be charged and change nothing while in the family.
    if (isViaCoParent(user)) {
      return {
        title:   'You already have family access',
        message: `You're part of ${user?.entitlement?.primaryName ? `${user.entitlement.primaryName}'s` : 'a'} family plan. To subscribe on your own account, leave the family first (Settings → Family).`,
      };
    }
    const sub = user?.subscription || {};
    if (!['active', 'trialing'].includes(sub.status) || !sub.plan || sub.plan === 'free') return null;
    // Billed by Stripe on the website — the store has no idea it exists.
    // ('store' is 'app_store'/'play_store' once an app purchase has taken over.)
    if (sub.stripeSubscriptionId && !['app_store', 'play_store'].includes(sub.store)) {
      // Information only — no link or "go buy it there": in the iOS app a call
      // to purchase outside the App Store breaks Guideline 3.1.1.
      return {
        title:   'Your plan is billed through our website',
        message: `Your subscription was set up on our website, so changes to it — including switching to ${planName} — are made there rather than in the app. Buying here would charge you for a second subscription.`,
      };
    }
    // Billed by the OTHER store: an iPhone cannot change a Google Play
    // subscription, nor an Android phone an App Store one.
    const here = isIOS() ? 'app_store' : 'play_store';
    if (['app_store', 'play_store'].includes(sub.store) && sub.store !== here) {
      return sub.store === 'app_store'
        ? { title: 'Change your plan on your Apple device', message: `Your subscription is billed through the App Store, so it can only be changed from an iPhone or iPad signed in to the same Apple ID — otherwise you would pay for two subscriptions. Open Dreamy Bedtime there to switch to ${planName}.` }
        : { title: 'Change your plan on your Android device', message: `Your subscription is billed through Google Play, so it can only be changed from an Android device signed in to the same Google account — otherwise you would pay for two subscriptions. Open Dreamy Bedtime there to switch to ${planName}.` };
    }
    return null;
  };

  const handleSubscribe = async (planId) => {
    if (!user) return navigate(`/register?redirect=${encodeURIComponent('/pricing')}`);
    if (planId === 'free') return navigate('/dashboard');
    // iOS App Store requires In-App Purchase for subscriptions (RevenueCat).
    if (shouldUseIap()) {
      // A purchase in this store can only ADD a subscription. Where the plan the
      // account already has lives somewhere else, buying here opened a second,
      // independent one — charged every month alongside the first.
      const blocked = appPurchaseBlock(planId);
      if (blocked) {
        await alert(blocked);
        return;
      }
      setLoading(planId);
      trackBeginCheckout(planId, billing);
      try {
        const r = await purchaseIap(planId, billing);
        if (!r.cancelled) { await refreshUser(); navigate(`/checkout/success?plan=${planId}&billing=${billing}`); }
      } catch (err) {
        await alert({ title: 'Purchase failed', message: err.message || 'Could not complete the purchase.', danger: true });
      } finally { setLoading(false); }
      return;
    }
    setLoading(planId);
    trackBeginCheckout(planId, billing);
    try {
      const affiliateCode = getAffiliateCookieCode() || '';
      const res = await API.post('/subscriptions/checkout', {
        plan: planId,
        billing,
        currency: pricing.currency,
        ...(affiliateCode && { affiliateCode }),
      });
      if (res.data.switched) {
        trackSubscriptionChanged(planId);
        (await refreshUser(), navigate('/dashboard?subscription=updated'));
      } else openCheckoutUrl(res.data.url);
    } catch (err) {
      // 403/409 are not failures: the server is explaining why this account
      // must not buy here (a co-parent, or a plan billed by an app store).
      const refused = [403, 409].includes(err.response?.status);
      await alert({
        title:   refused ? "You can't subscribe here" : 'Checkout failed',
        message: err.response?.data?.message || 'Failed to start checkout.',
        danger:  !refused,
      });
    } finally { setLoading(false); }
  };

  // entitledPlan, not the raw field. Stripe leaves subscription.plan as
  // 'family' when a subscription lapses and only changes status — so a churned
  // subscriber saw a DISABLED "Current Plan" button on the exact plan they were
  // trying to buy again, and could not re-subscribe to it at all. entitledPlan
  // collapses a lapsed subscription to 'free', which falls through to the normal
  // subscribe CTA.
  const currentPlan = entitledPlan(user);
  const currentInterval = user?.subscription?.billingInterval || 'month';
  const isCurrentPlanAndInterval = (planId) =>
    currentPlan === planId && (currentInterval === (isAnnual ? 'year' : 'month'));

  // A subscriber moving between plans is switching, not subscribing. Every card
  // said "Subscribe", so from inside the apps there was no visible difference
  // between buying a first plan and changing to a different one, and nothing
  // told a Basic subscriber that the Family button was the way to upgrade.
  const PLAN_RANK = { free: 0, basic: 1, family: 2 };
  const ctaLabel = (plan, price, per) => {
    if (!currentPlan || currentPlan === 'free') return `Subscribe — ${price}/${per}`;
    const verb = PLAN_RANK[plan.id] > PLAN_RANK[currentPlan] ? 'Upgrade to' : 'Switch to';
    return `${verb} ${plan.name} — ${price}/${per}`;
  };

  // Native store prices (localized by Apple/Google, incl. numeric amount) — native only.
  const [iapInfo, setIapInfo] = useState({});
  useEffect(() => {
    if (!shouldUseIap()) return;
    getIapProductInfo(['basic_monthly', 'basic_annual', 'family_monthly', 'family_annual', 'gift_story'])
      .then(setIapInfo).catch(() => {});
  }, []);
  // Per-product intro-offer eligibility for this Apple ID (iOS; {} elsewhere).
  const [introEligible, setIntroEligible] = useState({});
  useEffect(() => {
    if (!shouldUseIap()) return;
    const ids = ['basic_monthly', 'basic_annual', 'family_monthly', 'family_annual']
      .map(k => iapInfo[k]?.productId).filter(Boolean);
    if (ids.length) getIntroEligibility(ids).then(setIntroEligible).catch(() => {});
  }, [iapInfo]);

  // ── Free-trial eligibility (display only — the backend enforces the real rule) ──
  const trialCfg  = pricing.trial || {};
  const trialDays = trialCfg.days || 7;
  // Support the plans array; fall back to a legacy single `plan` value.
  const trialPlans = Array.isArray(trialCfg.plans)
    ? trialCfg.plans
    : (trialCfg.plan ? [trialCfg.plan] : []);
  const hasPaidSub = !!user
    && ['active', 'trialing'].includes(user?.subscription?.status)
    && user?.subscription?.plan !== 'free';
  // On the apps the free trial is the STORE's introductory offer, not ours: it
  // exists only if set up on that product (App Store Connect / Play Console),
  // and only for a store account that has not used one. The buttons used to
  // follow our own trial settings instead, so the app could say "Start 7-day
  // free trial" and the store charge in full — so on native the store decides.
  const storeTrial = (planId) => {
    const product = iapInfo[`${planId}_${isAnnual ? 'annual' : 'monthly'}`];
    if (!product?.intro || product.intro.price !== 0) return null;         // no free trial on this product
    if (isIOS() && introEligible[product.productId] !== true) return null; // this Apple ID used it already
    return product.intro;
  };
  const isTrialEligible = (planId) => {
    if (hasPaidSub) return false;
    if (shouldUseIap()) return !!storeTrial(planId);
    return !!trialCfg.enabled
      && trialPlans.includes(planId)
      && !user?.subscription?.hasUsedTrial;
  };
  // "7-day" — from the store's own offer on the apps.
  const trialLength = (planId) => {
    const intro = shouldUseIap() && storeTrial(planId);
    if (!intro) return `${trialDays}-day`;
    const n = intro.periodNumberOfUnits || 1;
    switch (intro.periodUnit) {
      case 'WEEK':  return n === 1 ? '7-day' : `${n}-week`;
      case 'MONTH': return `${n}-month`;
      case 'YEAR':  return `${n}-year`;
      default:      return `${n}-day`;
    }
  };


  // Price display helpers — prefer the real store price on native, else Stripe.
  const monthlyPrice = (planId) => iapInfo[`${planId}_monthly`]?.priceString || (planId === 'basic' ? pricing.formatted.basic : pricing.formatted.family);
  const annualPrice  = (planId) => iapInfo[`${planId}_annual`]?.priceString  || (planId === 'basic' ? pricing.formatted.basicAnnual : pricing.formatted.familyAnnual);
  // The annual card's big "/month" headline. It used to come ONLY from our
  // backend (annual ÷ 12), while the "Billed X/year" line beneath it came from
  // the store — so on the apps the two numbers could disagree. Apple's EUR tier
  // for Family annual might be €199.99 while our backend holds €340, giving
  // "€28.33/month · Billed €199.99/year": a headline that matches nothing the
  // customer pays. Worse, if geo-IP guessed a different currency than the App
  // Store account, the card mixed currencies outright. Store prices must be
  // accurate (Guideline 3.1.2), so derive the headline from the SAME annual
  // price that is actually billed.
  const monthlyEquiv = (planId) => {
    const a = iapInfo[`${planId}_annual`];
    if (a?.price != null && a?.currencyCode) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency', currency: a.currencyCode, maximumFractionDigits: 2,
        }).format(a.price / 12);
      } catch (_) { /* fall through to the backend figure */ }
    }
    return planId === 'basic' ? pricing.formatted.basicMonthlyEquiv : pricing.formatted.familyMonthlyEquiv;
  };
  const giftPrice = iapInfo.gift_story?.priceString || pricing.formatted.gift;
  const annualSavings = (planId) => {
    // On native, compute from the real store prices in the user's currency.
    const m = iapInfo[`${planId}_monthly`];
    const a = iapInfo[`${planId}_annual`];
    if (m?.price != null && a?.price != null) {
      const saved = m.price * 12 - a.price;
      if (saved <= 0) return '';
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: m.currencyCode, maximumFractionDigits: 0 }).format(saved);
      } catch (_) {
        return `${Math.round(saved)}`;
      }
    }
    const monthly = planId === 'basic' ? pricing.basic : pricing.family;
    const annual  = planId === 'basic' ? pricing.basicAnnual : pricing.familyAnnual;
    const saved   = monthly * 12 - annual;
    return `${pricing.symbol}${Number.isInteger(saved) ? saved : saved.toFixed(0)}`;
  };

  const store = paymentStoreName();
  const faqs = [
    { q: 'Can I cancel anytime?', a: store
        ? `Absolutely. Subscriptions bought in the app are managed by ${store}: cancel from your ${isIOS() ? 'Apple ID' : 'Google Play'} subscription settings (or Settings → Manage Subscription here) and you keep access until the end of your billing period.`
        : 'Absolutely. Cancel from your account settings with one click and you keep access until the end of your billing period. No questions asked, no penalties.' },
    { q: 'How does annual billing work?', a: `You pay once a year at a discounted rate — Basic saves you ${annualSavings('basic')}/year and Family saves you ${annualSavings('family')}/year compared to monthly billing. Your subscription renews annually unless you cancel before the renewal date.` },
    { q: 'Can I switch from monthly to annual (or vice versa)?', a: store
        ? `Yes. Select the billing period you want and tap Switch; ${store} handles the change so you are never charged twice.`
        : 'Yes. On the pricing page, select the billing period you want and click Subscribe. Switching to annual starts a new annual subscription and immediately cancels your existing monthly one — you won\'t be double-charged.' },
    { q: 'What happens to my stories if I downgrade or cancel?', a: 'Every story you\'ve ever generated stays in your library forever — even on the free plan. You just lose the ability to generate new ones beyond the free monthly limit.' },
    { q: 'Is there a free plan?', a: 'Yes — the Free plan gives you 5 stories every month, forever. No credit card required, no time limit. Upgrade only when you\'re convinced.' },
    { q: 'How many children can I have on one account?', a: 'Free: 1 child. Basic: up to 3 children. Family: unlimited children. Each child gets their own profile with their own interests, themes, and story history.' },
    { q: 'Can grandparents read the stories?', a: 'On the Family plan, you can create private share links for any story. Grandparents, relatives and friends can read it without needing an account — and even leave emoji reactions.' },
    { q: 'What languages are supported?', a: 'English, Spanish, French, Hindi, German, Portuguese, Arabic, Chinese, Japanese and Italian. All plans including Free get access to all languages.' },
    { q: 'What is smart narration?', a: 'Smart narration uses a high-quality text-to-speech voice that reads the story aloud in a warm, expressive tone. Basic gets 30 narrations/month, Family gets 40. Free users get browser read-aloud instead, which uses your device\'s built-in voice.' },
    { q: 'What is voice cloning and how does it work?', a: 'Voice cloning is a Family-plan exclusive feature. You record 30 seconds of yourself reading a short passage — Dreamy Bedtime instantly clones your voice. From that point on, every story narration can use your actual voice. Your children hear bedtime stories in Mum\'s or Dad\'s voice even when you\'re not in the room.' },
    { q: 'What are Photo Illustrations?', a: `Photo Illustrations put your child's actual face in every generated story image. Upload one clear photo of your child in their profile settings, and we create illustrations where they are the main character — same face, hair, eyes, and skin tone in every scene. Basic plan includes 3 per month, Family includes 6. You can buy top-up packs of +3 for ${pricing.topUpPacks?.photo?.priceDisplay || '$4.99'} at any time.` },
    { q: 'Is my payment information secure?', a: store
        ? `Purchases in the app are made through ${store} with your ${isIOS() ? 'Apple ID' : 'Google account'}. We never see or store your payment details.`
        : 'All payments are processed by Stripe, the same payment provider used by Amazon, Shopify, and millions of other businesses. We never store your card details.' },
  ];

  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SEO
        title="Pricing Plans — Bedtime Stories for Every Family"
        description="Start free with 5 stories a month. Upgrade to Basic or Family for unlimited personalised bedtime stories, magical illustrations, voice narration, and more."
        canonical="/pricing"
      />
      <SchemaMarkup schema={[
        buildFaqSchema(PRICING_FAQS),
        buildProductSchema({ name: 'Dreamy Bedtime Basic', description: 'Unlimited personalised bedtime stories with illustrations and narration.', price: pricing.basic?.monthly ?? '4.99' }),
        buildProductSchema({ name: 'Dreamy Bedtime Family', description: 'Unlimited personalised bedtime stories for all your children with all premium features.', price: pricing.family?.monthly ?? '7.99' }),
      ]} />
      <Navbar />
      <ExitIntentPopup />

      {/* Hero */}
      <section className="bg-dream text-white" style={{ padding: '64px 0 48px' }}>
        <div className="container text-center" style={{ maxWidth: '640px' }}>
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill">💎 Pricing</span>
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(32px,5vw,50px)' }}>
            Simple, honest pricing
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: '18px', marginBottom: '32px' }}>
            Start free forever. Upgrade when bedtime becomes your favourite part of the day.
          </p>

          {/* ── Billing toggle ── */}
          <div className="d-inline-flex align-items-center rounded-pill p-1"
            style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <button
              className={`btn btn-sm rounded-pill px-4 fw-semibold ${!isAnnual ? 'btn-light' : ''}`}
              style={isAnnual ? { color: 'rgba(255,255,255,0.7)', background: 'transparent', border: 'none' } : { fontSize: '14px' }}
              onClick={() => { setBilling('monthly'); trackPricingBillingToggled('monthly'); }}
            >
              Monthly
            </button>
            <button
              className={`btn btn-sm rounded-pill px-4 fw-semibold ${isAnnual ? 'btn-light' : ''}`}
              style={!isAnnual ? { color: 'rgba(255,255,255,0.7)', background: 'transparent', border: 'none' } : { fontSize: '14px' }}
              onClick={() => { setBilling('annual'); trackPricingBillingToggled('annual'); }}
            >
              Annual
              <span className="badge rounded-pill ms-2 fw-bold"
                style={{ background: '#4ade80', color: '#14532d', fontSize: '11px' }}>
                Save up to {annualSavings('family')}
              </span>
            </button>
          </div>


          {isAnnual && (
            <p className="mt-3 mb-0" style={{ color: '#4ade80', fontSize: '14px', fontWeight: 600 }}>
              🎉 You save {annualSavings('basic')} on Basic · {annualSavings('family')} on Family compared to monthly billing
            </p>
          )}
        </div>
      </section>

      <div className="container py-5" style={{ maxWidth: '1100px' }}>

        {/* Plan cards */}
        <div className="row g-4 justify-content-center mb-5">
          {plans.map(plan => (
            <div className="col-md-4" key={plan.id}>
              <div className="card border-0 shadow h-100"
                style={{ borderRadius: '24px', background: plan.bg, border: plan.id === 'family' ? `2px solid ${plan.color}` : '1px solid #e9ecef', overflow: 'hidden' }}>
                {plan.tag && (
                  <div className="text-center py-2" style={{ background: plan.color }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '13px' }}>{plan.tag}</span>
                  </div>
                )}
                <div className="p-4 d-flex flex-column h-100">
                  <div className="mb-4">
                    <div className="fw-bold mb-1" style={{ color: plan.color, fontFamily: "'Fredoka One',cursive", fontSize: '24px' }}>
                      {plan.name}
                    </div>

                    {/* ── Price display ── */}
                    {plan.id === 'free' ? (
                      <div className="d-flex align-items-baseline gap-1">
                        <span className="fw-bold" style={{ fontSize: '44px', color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>{pricing.symbol}0</span>
                        <span style={{ color: 'var(--text-soft)', fontSize: '15px' }}>/forever</span>
                      </div>
                    ) : isAnnual ? (
                      <>
                        <div className="d-flex align-items-baseline gap-1 flex-wrap">
                          <span className="fw-bold" style={{ fontSize: '44px', color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
                            {monthlyEquiv(plan.id)}
                          </span>
                          <span style={{ color: 'var(--text-soft)', fontSize: '15px' }}>/month</span>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span style={{ color: 'var(--text-muted2)', fontSize: '13px' }}>
                            Billed <strong style={{ color: 'var(--text-main)' }}>{annualPrice(plan.id)}</strong>/year
                          </span>
                          <span className="badge rounded-pill"
                            style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 700 }}>
                            Save {annualSavings(plan.id)}
                          </span>
                        </div>
                        <div className="mt-1" style={{ fontSize: '12px', color: '#9ca3af' }}>
                          <span style={{ textDecoration: 'line-through' }}>{monthlyPrice(plan.id)}/mo</span> if billed monthly
                        </div>
                      </>
                    ) : (
                      <div className="d-flex align-items-baseline gap-1">
                        <span className="fw-bold" style={{ fontSize: '44px', color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
                          {monthlyPrice(plan.id)}
                        </span>
                        <span style={{ color: 'var(--text-soft)', fontSize: '15px' }}>/month</span>
                      </div>
                    )}
                  </div>

                  <ul className="list-unstyled mb-4 flex-grow-1">
                    {plan.features.map(f => (
                      <li key={f.text} className="d-flex align-items-start gap-2 mb-2">
                        <i className={`bi ${f.ok ? 'bi-check-circle-fill' : 'bi-x-circle'} mt-1`}
                          style={{ color: f.ok ? '#4ade80' : 'var(--border-purple2)', flexShrink: 0, fontSize: '14px' }}></i>
                        <span style={{ color: f.ok ? 'var(--text-body)' : 'var(--text-soft)', fontSize: '13px' }}>{f.text}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlanAndInterval(plan.id) ? (
                    <button className="btn btn-outline-secondary rounded-pill w-100" disabled>
                      <i className="bi bi-check2 me-2"></i>Current Plan
                    </button>
                  ) : currentPlan === plan.id && plan.id !== 'free' ? (
                    // Same plan, different interval
                    <button
                      className={`btn ${plan.btnClass} rounded-pill w-100 fw-semibold`}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={!!loading}>
                      {loading === plan.id
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Loading…</>
                        : isAnnual
                          ? `Switch to Annual — ${annualPrice(plan.id)}/yr`
                          : `Switch to Monthly — ${monthlyPrice(plan.id)}/mo`}
                    </button>
                  ) : (
                    <>
                      <button
                        className={`btn ${plan.btnClass} rounded-pill w-100 fw-semibold`}
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={!!loading}>
                        {loading === plan.id
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Loading…</>
                          : plan.id === 'free'
                            ? 'Get Started Free'
                            : isTrialEligible(plan.id)
                              ? `Start ${trialLength(plan.id)} free trial`
                              : isAnnual
                                ? ctaLabel(plan, annualPrice(plan.id), 'yr')
                                : ctaLabel(plan, monthlyPrice(plan.id), 'mo')}
                      </button>
                      {isTrialEligible(plan.id) && (
                        <div className="text-center small mt-2" style={{ color: 'var(--text-muted2)' }}>
                          then {isAnnual ? `${annualPrice(plan.id)}/yr` : `${monthlyPrice(plan.id)}/mo`} · cancel anytime
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Auto-renewable subscription terms, in the purchase flow — required
            by App Store Guideline 3.1.2(c): each subscription's title, length
            and price, and functional links to the Terms of Use (EULA) and the
            Privacy Policy. Prices are the store's own, as charged. */}
        {shouldUseIap() && (
          <div className="rounded-4 p-3 p-md-4 mb-5"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border-purple)', color: 'var(--text-muted2)', fontSize: '13px', lineHeight: 1.6 }}>
            <div className="fw-semibold mb-2" style={{ color: 'var(--text-main)', fontSize: '14px' }}>Subscription details</div>
            <ul className="mb-2 ps-3">
              {['basic', 'family'].map(id => {
                const name = plans.find(p => p.id === id)?.name || id;
                return (
                  <React.Fragment key={id}>
                    <li>Dreamy Bedtime {name} — Monthly: {monthlyPrice(id)} per month, 1-month auto-renewing subscription</li>
                    <li>Dreamy Bedtime {name} — Annual: {annualPrice(id)} per year ({monthlyEquiv(id)}/month), 1-year auto-renewing subscription</li>
                  </React.Fragment>
                );
              })}
            </ul>
            <p className="mb-2">
              Payment is charged to your {isIOS() ? 'Apple ID' : 'Google Play'} account at confirmation of purchase.
              A subscription renews automatically at the same price and length unless it is cancelled at least
              24 hours before the end of the current period, and your account is charged for renewal within the
              24 hours before the period ends. Manage or cancel it at any time in your {isIOS() ? 'App Store' : 'Google Play'} account
              settings.{['basic', 'family'].some(isTrialEligible) && ' If a free trial is offered, it becomes a paid subscription when the trial ends unless you cancel at least 24 hours before; any unused part of a trial is forfeited when you buy a subscription.'}
            </p>
            <div className="fw-semibold">
              <Link to="/terms" style={{ color: 'var(--text-main)', textDecoration: 'underline' }}>Terms of Use (EULA)</Link>
              <span className="mx-2">·</span>
              <Link to="/privacy" style={{ color: 'var(--text-main)', textDecoration: 'underline' }}>Privacy Policy</Link>
            </div>
          </div>
        )}

        {/* Annual savings callout (only shown when monthly is selected) */}
        {!isAnnual && (
          <div className="card border-0 shadow-sm mb-5 p-4"
            style={{ borderRadius: '20px', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '2px solid #86efac' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span style={{ fontSize: '24px' }}>💡</span>
                  <h6 className="fw-bold mb-0" style={{ color: '#14532d' }}>Save more with annual billing</h6>
                </div>
                <p className="mb-0" style={{ color: '#166534', fontSize: '14px' }}>
                  Switch to annual and save <strong>{annualSavings('basic')}</strong> on Basic or <strong>{annualSavings('family')}</strong> on Family — pay once, covered for a full year.
                </p>
              </div>
              <button className="btn fw-bold rounded-pill px-4"
                style={{ background: '#16a34a', color: 'white', border: 'none', whiteSpace: 'nowrap' }}
                onClick={() => setBilling('annual')}>
                Show Annual Prices
              </button>
            </div>
          </div>
        )}

        {/* Gift a story callout */}
        <div className="card border-0 shadow-sm mb-5 p-4 p-lg-5" style={{ borderRadius: '24px', background: 'linear-gradient(135deg,#2d1b69,#6f42c1)', overflow: 'hidden' }}>
          <div className="row align-items-center g-4">
            <div className="col-md-8">
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>🎁</div>
              <h4 className="fw-bold text-white mb-2" style={{ fontFamily: "'Fredoka One',cursive" }}>Don't want a subscription? Gift a story instead.</h4>
              <p style={{ color: '#c9a8ff', marginBottom: 0, fontSize: '15px' }}>
                Send a one-time personalised bedtime story to any child — grandchild, niece, nephew, or friend — for just {giftPrice}. No account needed.
              </p>
            </div>
            <div className="col-md-4 text-md-end">
              <Link to="/gift" className="btn btn-warning btn-lg rounded-pill fw-bold px-4">
                🎁 Gift a Story — {giftPrice}
              </Link>
            </div>
          </div>
        </div>

        {/* Feature comparison table */}
        <div className="card border-0 shadow-sm mb-5" style={{ borderRadius: '20px', overflow: 'hidden' }}>
          <div className="p-4 border-bottom" style={{ background: 'var(--purple-section)' }}>
            <h4 className="fw-bold mb-0" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>Full Feature Comparison</h4>
          </div>
          <div className="table-responsive">
            <table className="table mb-0" style={{ fontSize: '14px' }}>
              <thead>
                <tr style={{ background: 'var(--header-bg)' }}>
                  <th className="py-3 px-4" style={{ color: 'var(--text-main)', fontWeight: 700, width: '40%' }}>Feature</th>
                  <th className="py-3 text-center" style={{ color: 'var(--text-muted2)' }}>Free</th>
                  <th className="py-3 text-center" style={{ color: '#6f42c1' }}>Basic</th>
                  <th className="py-3 text-center" style={{ color: '#fd7e14' }}>Family</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Stories per month', '5', '60', '100'],
                  ['Child profiles', '1', 'Up to 3', 'Unlimited'],
                  ['Story library', '✓', '✓', '✓'],
                  ['Magical illustrations', '✓', '✓', '✓'],
                  ['Illustration regenerations/mo', '—', '20', '40'],
                  ['All 11 languages', '✓', '✓', '✓'],
                  ['Browser read-aloud', '✓', '✓', '✓'],
                  ['Smart narrations per month', '—', '30', '40'],
                  ['Seasonal & holiday themes', '✓', '✓', '✓'],
                  ['Story Series (multi-chapter)', '—', '✓', '✓'],
                  ['Story Quiz (comprehension questions)', '—', '✓', '✓'],
                  ['Real-world learning topics', '—', '✓', '✓'],
                  ['PDF export', '—', '✓', '✓'],
                  ['Bedtime reminders', '✓', '✓', '✓'],
                  ['Weekly story digest', '✓', '✓', '✓'],
                  ['Theme scheduler (per day)', '—', '✓', '✓'],
                  ['Multi-theme per story', '—', '✓', '✓'],
                  ['Grandparent sharing links', '—', '—', '✓'],
                  ['Voice cloning narrations/mo', '—', '—', '5'],
                  ['Emoji reactions & view counts', '—', '—', '✓'],
                  ['Early access to new features', '—', '—', '✓'],
                  ['Priority support', '—', '—', '✓'],
                  ['📸 Photo illustrations (your child\'s face in stories)', '—', '3/mo', '6/mo'],
                  ['Buy extra credits (top-ups)', '—', '✓', '✓'],
                  ['Strict Mode (no scary content)', '✓', '✓', '✓'],
                  ['Story Approval Queue', '✓', '✓', '✓'],
                  ['Reading Age Calibration (auto + manual override)', '✓', '✓', '✓'],
                ].map(([feat, free, basic, family]) => (
                  <tr key={feat} style={{ borderBottom: '1px solid var(--border-purple)' }}>
                    <td className="py-3 px-4" style={{ color: 'var(--text-body)', fontWeight: 500 }}>{feat}</td>
                    {[free, basic, family].map((val, i) => (
                      <td key={i} className="py-3 text-center">
                        {val === '✓' ? <i className="bi bi-check-circle-fill" style={{ color: '#4ade80', fontSize: '16px' }}></i>
                          : val === '—' ? <span style={{ color: 'var(--text-soft)', fontSize: '18px' }}>—</span>
                          : <span style={{ color: 'var(--text-body)', fontWeight: 600 }}>{val}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Annual billing row */}
                <tr style={{ background: 'var(--green-section)', borderBottom: '1px solid var(--border-green)' }}>
                  <td className="py-3 px-4 fw-semibold" style={{ color: 'var(--text-green)' }}>
                    Annual billing (best value)
                  </td>
                  <td className="py-3 text-center"><span style={{ color: 'var(--text-soft)', fontSize: '18px' }}>—</span></td>
                  <td className="py-3 text-center">
                    <span className="badge rounded-pill" style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 700 }}>
                      Save {annualSavings('basic')}/yr
                    </span>
                  </td>
                  <td className="py-3 text-center">
                    <span className="badge rounded-pill" style={{ background: '#dcfce7', color: '#15803d', fontSize: '11px', fontWeight: 700 }}>
                      Save {annualSavings('family')}/yr
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Top-Up Packs */}
        <div className="mb-5">
          <div className="text-center mb-4">
            <span className="badge rounded-pill px-3 py-2 mb-3"
              style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '13px', fontWeight: 700 }}>
              ⚡ Optional Add-Ons
            </span>
            <h3 className="fw-bold mb-2" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: '28px' }}>
              Need a little extra this month?
            </h3>
            <p style={{ color: 'var(--text-muted2)', fontSize: '15px', maxWidth: '500px', margin: '0 auto' }}>
              One-time packs — buy only what you need, credits added instantly, no subscription required.
            </p>
          </div>

          <div className="row g-3">
            {(() => {
              const topUpMeta = {
                story:     { accent: '#6f42c1', bg: '#f8f5ff', border: '#d4b8ff', grant: '+10 stories',            plans: 'Basic & Family' },
                narration: { accent: '#0d6efd', bg: '#f0f7ff', border: '#b8d4ff', grant: '+10 narrations',          plans: 'Basic & Family' },
                voice:     { accent: '#ea580c', bg: '#fff7ed', border: '#fdba74', grant: '+5 voice narrations',     plans: 'Family only' },
                regen:     { accent: '#059669', bg: '#f0fdf4', border: '#6ee7b7', grant: '+10 regenerations',       plans: 'Basic & Family' },
                photo:     { accent: '#db2777', bg: '#fdf2f8', border: '#f9a8d4', grant: '+3 photo illustrations', plans: 'All plans' },
              };
              return Object.entries(pricing.topUpPacks).map(([key, pack]) => {
                const meta = topUpMeta[key];
                if (!meta) return null;
                return (
                  <div className="col-sm-6 col-lg-4" key={pack.id}>
                    <div className="h-100 p-4 rounded-4 d-flex flex-column"
                      style={{ background: meta.bg, border: `1.5px solid ${meta.border}` }}>
                      <div className="d-flex align-items-center gap-3 mb-3">
                        <div className="rounded-3 d-flex align-items-center justify-content-center"
                          style={{ width: '48px', height: '48px', background: 'white', fontSize: '24px', flexShrink: 0, boxShadow: `0 0 0 2px ${meta.border}` }}>
                          {pack.emoji}
                        </div>
                        <div>
                          <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '15px', lineHeight: 1.2 }}>{pack.label}</div>
                          <div className="fw-bold" style={{ color: meta.accent, fontSize: '20px', fontFamily: "'Fredoka One',cursive", lineHeight: 1.2 }}>
                            {pack.priceDisplay}
                            <span style={{ fontSize: '13px', color: 'var(--text-soft)', fontFamily: 'inherit', fontWeight: 400 }}> one-time</span>
                          </div>
                        </div>
                      </div>
                      <div className="mb-2 px-3 py-2 rounded-3 fw-bold text-center"
                        style={{ background: 'var(--card-bg)', color: meta.accent, fontSize: '14px', border: `1px solid ${meta.border}` }}>
                        {meta.grant}
                      </div>
                      <p style={{ color: 'var(--text-muted2)', fontSize: '13px', marginBottom: '12px', flexGrow: 1 }}>{pack.description}</p>
                      <div className="d-flex align-items-center gap-1 mt-auto">
                        <i className="bi bi-person-check" style={{ color: meta.accent, fontSize: '12px' }}></i>
                        <span style={{ color: 'var(--text-soft)', fontSize: '11px', fontWeight: 600 }}>{meta.plans}</span>
                      </div>
                    </div>
                  </div>
                );
              });
            })()}

            {/* Sixth cell: reassurance note */}
            <div className="col-sm-6 col-lg-4">
              <div className="h-100 p-4 rounded-4 d-flex flex-column justify-content-center"
                style={{ background: 'var(--purple-section)', border: '1.5px solid var(--border-purple2)' }}>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>🔒</div>
                <h6 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Credits never expire</h6>
                <p style={{ color: 'var(--text-muted2)', fontSize: '13px', marginBottom: '12px' }}>
                  Top-up credits stack on top of your monthly allowance and carry over indefinitely — they don't reset at month end.
                </p>
                <p style={{ color: 'var(--text-muted2)', fontSize: '13px', marginBottom: 0 }}>
                  All payments are one-time charges {paymentStoreName() ? `through ${paymentStoreName()}` : 'via Stripe'}. No hidden fees, no auto-renewals on top-ups.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Voice cloning callout */}
        <div className="card border-0 shadow-sm mb-5 p-4 p-lg-5" style={{ borderRadius: '24px', background: 'linear-gradient(135deg,#fff7ed,#fed7aa)', border: '2px solid #fdba74', overflow: 'hidden' }}>
          <div className="row align-items-center g-4">
            <div className="col-md-8">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span style={{ fontSize: '32px' }}>🎙️</span>
                <span className="badge rounded-pill px-3 py-2" style={{ background: '#ea580c', color: 'white', fontSize: '12px', fontWeight: 700 }}>Family Plan Exclusive</span>
              </div>
              <h4 className="fw-bold mb-2" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)' }}>
                Voice Cloning — Stories in Your Own Voice
              </h4>
              <p style={{ color: 'var(--text-muted2)', marginBottom: '12px', fontSize: '15px', lineHeight: 1.7 }}>
                Record 30 seconds of your voice — Dreamy Bedtime clones it instantly. Every story your child hears is narrated by <strong>you</strong> — even when you're not there.
              </p>
              <div className="d-flex flex-wrap gap-3">
                {['30s recording', 'Instant cloning', 'Unlimited use', 'Delete anytime'].map(t => (
                  <span key={t} className="badge rounded-pill px-3 py-2"
                    style={{ background: 'rgba(234,88,12,0.12)', color: '#9a3412', fontSize: '12px', fontWeight: 600 }}>
                    ✓ {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="col-md-4 text-md-end">
              <button className="btn btn-lg rounded-pill fw-bold px-4"
                style={{ background: 'linear-gradient(135deg,#ea580c,#f97316)', color: 'white', border: 'none' }}
                onClick={() => handleSubscribe('family')}>
                {isTrialEligible('family')
                  ? `Start ${trialLength('family')} free trial`
                  : isAnnual
                    ? `Get Family Annual — ${annualPrice('family')}/yr`
                    : `Get Family Plan — ${monthlyPrice('family')}/mo`}
              </button>
              <div className="mt-2" style={{ fontSize: '12px', color: '#c2410c' }}>
                {isTrialEligible('family')
                  ? `then ${isAnnual ? `${annualPrice('family')}/yr` : `${monthlyPrice('family')}/mo`} · cancel anytime`
                  : `${isAnnual ? `Save ${annualSavings('family')} vs monthly · ` : ''}Includes everything in Basic too`}
              </div>
            </div>
          </div>
        </div>

        {/* Photo Illustration callout */}
        <div className="card border-0 shadow-sm mb-5 p-4 p-lg-5" style={{ borderRadius: '24px', background: 'linear-gradient(135deg,#fdf2f8,#fce7f3)', border: '2px solid #f9a8d4', overflow: 'hidden' }}>
          <div className="row align-items-center g-4">
            <div className="col-md-8">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span style={{ fontSize: '32px' }}>📸</span>
                <span className="badge rounded-pill px-3 py-2" style={{ background: '#db2777', color: 'white', fontSize: '12px', fontWeight: 700 }}>Basic & Family Plans</span>
              </div>
              <h4 className="fw-bold mb-2" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)' }}>
                Your Child, the Hero — Literally
              </h4>
              <p style={{ color: 'var(--text-muted2)', marginBottom: '12px', fontSize: '15px', lineHeight: 1.7 }}>
                Upload one photo of your child and every illustration will feature <strong>their actual face</strong> — same hair, same eyes, same smile. Their appearance stays consistent across every single scene.
              </p>
              <div className="d-flex flex-wrap gap-3 mb-3">
                {['Face-accurate illustrations', 'Consistent across scenes', 'One photo is all it takes', 'Works with any photo'].map(t => (
                  <span key={t} className="badge rounded-pill px-3 py-2"
                    style={{ background: 'rgba(219,39,119,0.1)', color: '#9d174d', fontSize: '12px', fontWeight: 600 }}>
                    ✓ {t}
                  </span>
                ))}
              </div>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="p-3 rounded-3 text-center" style={{ background: 'rgba(219,39,119,0.08)', minWidth: '90px' }}>
                  <div className="fw-bold" style={{ color: '#db2777', fontSize: '22px', fontFamily: "'Fredoka One',cursive" }}>3/mo</div>
                  <div style={{ color: '#9d174d', fontSize: '11px', fontWeight: 600 }}>Basic Plan</div>
                </div>
                <div className="p-3 rounded-3 text-center" style={{ background: 'rgba(219,39,119,0.08)', minWidth: '90px' }}>
                  <div className="fw-bold" style={{ color: '#db2777', fontSize: '22px', fontFamily: "'Fredoka One',cursive" }}>6/mo</div>
                  <div style={{ color: '#9d174d', fontSize: '11px', fontWeight: 600 }}>Family Plan</div>
                </div>
                <div className="p-3 rounded-3 text-center" style={{ background: 'rgba(219,39,119,0.08)', minWidth: '90px' }}>
                  <div className="fw-bold" style={{ color: '#db2777', fontSize: '22px', fontFamily: "'Fredoka One',cursive" }}>+3</div>
                  <div style={{ color: '#9d174d', fontSize: '11px', fontWeight: 600 }}>Top-up {pricing.topUpPacks?.photo?.priceDisplay || '$4.99'}</div>
                </div>
              </div>
            </div>
            <div className="col-md-4 text-md-end">
              <button className="btn btn-lg rounded-pill fw-bold px-4 mb-2 w-100"
                style={{ background: 'linear-gradient(135deg,#db2777,#ec4899)', color: 'white', border: 'none' }}
                onClick={() => handleSubscribe('basic')}>
                {isTrialEligible('basic')
                  ? `Start ${trialLength('basic')} free trial`
                  : isAnnual
                    ? `Get Basic Annual — ${annualPrice('basic')}/yr`
                    : `Get Basic Plan — ${monthlyPrice('basic')}/mo`}
              </button>
              <div style={{ fontSize: '12px', color: '#9d174d', textAlign: 'center' }}>
                {isTrialEligible('basic')
                  ? `then ${isAnnual ? `${annualPrice('basic')}/yr` : `${monthlyPrice('basic')}/mo`} · cancel anytime`
                  : `${isAnnual ? `Save ${annualSavings('basic')} vs monthly · ` : ''}Photo uploads in child settings`}
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="row g-3 mb-5">
          {[
            { quote: 'My kids ask for Dreamy Bedtime every night. Worth every penny.', name: 'Sarah M.', role: 'Mom of 2' },
            { quote: 'The Family plan pays for itself the first time grandma uses the sharing link.', name: 'James T.', role: 'Dad of 3' },
            { quote: 'Hindi stories for my daughter — I never expected this to exist.', name: 'Priya K.', role: 'Mom of twins' },
          ].map(t => (
            <div className="col-md-4" key={t.name}>
              <div className="card border-0 h-100 p-4" style={{ borderRadius: '16px', background: 'var(--card-bg)', border: '1px solid var(--border-purple)' }}>
                <div style={{ color: '#f9c74f', marginBottom: '8px' }}>★★★★★</div>
                <p style={{ color: 'var(--text-muted2)', fontSize: '14px', fontStyle: 'italic', marginBottom: '12px' }}>"{t.quote}"</p>
                <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '13px' }}>{t.name} <span style={{ color: 'var(--text-soft)', fontWeight: 400 }}>· {t.role}</span></div>
              </div>
            </div>
          ))}
        </div>

        {/* Not-ready lead capture — sends 3 sample stories by email */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm p-4 p-md-5 text-center" style={{ borderRadius: '24px', background: 'var(--card-bg)', border: '1.5px solid var(--border-purple)' }}>
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>🌙</div>
              <h4 className="fw-bold mb-2" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)' }}>
                Not ready to decide yet?
              </h4>
              <p style={{ color: 'var(--text-muted2)', fontSize: '14px', maxWidth: '440px', margin: '0 auto 18px' }}>
                Get 3 free sample stories by email — one for every age — and see the quality for yourself before signing up.
              </p>
              <LeadEmailForm source="pricing_page" askName askAge buttonLabel="📬 Send my 3 free stories" />
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <h3 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: '32px' }}>
              Frequently Asked Questions
            </h3>
            <div className="accordion" id="faqAccordion">
              {faqs.map((item, i) => (
                <div className="accordion-item border-0 mb-2 shadow-sm" style={{ borderRadius: '14px', overflow: 'hidden' }} key={i}>
                  <h2 className="accordion-header">
                    {/* --card-bg, not hardcoded white: paired with --text-main
                        (pale lavender in dark mode) a white surface made every
                        FAQ question unreadable. */}
                    <button
                      className="accordion-button collapsed fw-semibold"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#faq${i}`}
                      style={{ borderRadius: '14px', color: 'var(--text-main)', background: 'var(--card-bg)', fontSize: '15px' }}>
                      {item.q}
                    </button>
                  </h2>
                  <div id={`faq${i}`} className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                    <div className="accordion-body" style={{ color: 'var(--text-muted2)', fontSize: '14px', lineHeight: 1.8 }}>{item.a}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-5 p-4 rounded-3" style={{ background: 'var(--purple-section)' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🌙</div>
              <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Still have questions?</h5>
              <p style={{ color: '#6f42c1', marginBottom: '16px', fontSize: '15px' }}>We're a small team and we actually respond.</p>
              <a href="mailto:support@dreamybedtime.com" className="btn btn-dream rounded-pill px-4">
                <i className="bi bi-envelope me-2"></i>Email us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
